import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  Link,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DataTable from "../../../Components/Common/datatable/DataTable";
import ButtonComponent from "../../../Components/Common/button/Button";
import { MessagePopup } from "../../../Components/Common/messagePopup/MessagePopup";

const blankIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ProfitCenterAssignmentEditor = ({
  user,
  profitCenterOptions,
  userMappings,
  loadingMappings,
  saving,
  onBack,
  onSave,
  onBulkToggle,
}) => {
  const gridRef = useRef(null);

  // All mappings for this user (enabled + disabled) - source of truth
  const [allPCs, setAllPCs] = useState([]);
  // What the admin has selected in the autocomplete but not yet enabled
  const [pendingSelections, setPendingSelections] = useState([]);
  // Profit center country codes of currently selected table rows
  const [selectedTableRows, setSelectedTableRows] = useState([]);
  // Tracks which action is in-flight so each button independently reflects API status
  const [actionType, setActionType] = useState(null); // 'enable' | 'disable' | null
  // Currently selected region (step 1); null means region-selection mode
  const [selectedRegion, setSelectedRegion] = useState(null);

  // Clear actionType when Redux saving resolves
  useEffect(() => {
    if (!saving) setActionType(null);
  }, [saving]);

  // Sync local state from Redux whenever userMappings changes (initial load or post-save)
  useEffect(() => {
    setAllPCs(userMappings || []);
    setPendingSelections([]);
    setSelectedTableRows([]);
    setSelectedRegion(null);
  }, [userMappings]);

  // Only enabled mappings are shown in the table
  const assignedPCs = useMemo(
    () => allPCs.filter((item) => item.isEnabled),
    [allPCs],
  );

  //Get distict region
  const distinctRegions = useMemo(() => {
    const regionsSet = new Set(profitCenterOptions.map((opt) => opt.region).filter(Boolean));
    return Array.from(regionsSet);
  }, [profitCenterOptions]);

  // Autocomplete options:
  // - Region mode (selectedRegion === null): returns the list of distinct region strings
  // - PC mode (selectedRegion set): returns full profitCenterOptions objects for the selected
  //   region that are not already assigned, deduplicated by description
  const availableOptions = useMemo(() => {
    if (!selectedRegion) {
      return distinctRegions;
    }
    const activeDescs = new Set(assignedPCs.map((item) => item.profitCenterDescription));
    const filtered = profitCenterOptions.filter(
      (opt) => opt.region === selectedRegion && !activeDescs.has(opt.profitCenterDescription),
    );
    // Deduplicate by description to prevent phantom multi-select on single click
    const seen = new Set();
    return filtered.filter((opt) => {
      const key = opt.profitCenterDescription;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [profitCenterOptions, assignedPCs, selectedRegion, distinctRegions]);

  // ── Autocomplete change handler ──────────────────────────────────────────────
  const handleAutocompleteChange = useCallback(
    (_, newValues) => {
      if (!selectedRegion) {
        // Region mode: user picks a single region string → advance to PC mode
        const picked = newValues[newValues.length - 1];
        if (picked && typeof picked === "string") {
          setSelectedRegion(picked);
          setPendingSelections([]);
        }
        return;
      }
      // PC mode: existing Select All / individual PC toggle logic
      const selectAllChosen = newValues.some((v) => v === "Select All");
      if (selectAllChosen) {
        const allSelected = pendingSelections.length === availableOptions.length && availableOptions.length > 0;
        setPendingSelections(allSelected ? [] : [...availableOptions]);
      } else {
        setPendingSelections(newValues.filter((v) => v !== "Select All"));
      }
    },
    [selectedRegion, pendingSelections, availableOptions],
  );

  // ── Enable ──────────────────────────────────────────────────────────────────
  const handleEnable = async () => {
    const result = await MessagePopup({
      title: "Confirm Enable",
      html: `<p>Are you sure you want to assign <strong>${pendingSelections.length}</strong> profit center(s) to this user?</p>`,
      showCancelButton: true,
      confirmButtonText: "Enable",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#000000",
      cancelButtonColor: "#808080",
    });

    if (!result.isConfirmed) return;

    setActionType("enable");
    console.log("Enabling profit centers:", pendingSelections);

    const enabledDescs = new Set(pendingSelections.map((opt) => opt.profitCenterDescription));
    const existingDescs = new Set(allPCs.map((item) => item.profitCenterDescription));

    // Previously disabled entries that already have a DB row — toggle them back on
    const toReactivate = allPCs.filter(
      (item) => enabledDescs.has(item.profitCenterDescription) && item.id != null,
    );

    // Truly new entries that have never been saved — must be INSERTed
    const brandNew = pendingSelections.filter(
      (opt) => !existingDescs.has(opt.profitCenterDescription),
    );

    // Optimistic local update
    const updatedExisting = allPCs.map((item) =>
      enabledDescs.has(item.profitCenterDescription) ? { ...item, isEnabled: true } : item,
    );
    const newEntries = brandNew.map((opt) => ({
      profitCenterCountry: opt.profitCenterCountry,
      profitCenterDescription: opt.profitCenterDescription,
      region: opt.region ?? null,
      isEnabled: true,
    }));

    setAllPCs([...updatedExisting, ...newEntries]);
    setPendingSelections([]);

    // Re-activate existing disabled rows via bulk toggle (PUT — no INSERT)
    if (toReactivate.length > 0) {
      onBulkToggle({ mappingIds: toReactivate.map((item) => item.id), isEnabled: true });
    }
    // Insert only genuinely new rows
    if (brandNew.length > 0) {
      onSave(
        brandNew.map(({ profitCenterCountry, profitCenterDescription, region }) => ({
          profitCenterCountry,
          profitCenterDescription,
          region: region ?? null,
          isEnabled: true,
        })),
      );
    }
  };

  // ── Disable ─────────────────────────────────────────────────────────────────
  const handleDisable = async () => {
    const result = await MessagePopup({
      title: "Confirm Disable",
      html: `<p>Are you sure you want to disable <strong>${selectedTableRows.length}</strong> profit center(s) for this user?</p>`,
      showCancelButton: true,
      confirmButtonText: "Disable",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#808080",
    });

    if (!result.isConfirmed) return;

    setActionType("disable");
    const disabledDescs = new Set(selectedTableRows);

    // Collect the numeric mapping IDs for the selected rows
    const mappingIds = allPCs
      .filter((item) => disabledDescs.has(item.profitCenterDescription) && item.id != null)
      .map((item) => item.id);

    // Optimistic local update for immediate visual feedback
    const updated = allPCs.map((item) =>
      disabledDescs.has(item.profitCenterDescription)
        ? { ...item, isEnabled: false }
        : item,
    );

    setAllPCs(updated);
    setSelectedTableRows([]);
    if (gridRef.current?.api) {
      gridRef.current.api.deselectAll();
    }

    onBulkToggle({ mappingIds, isEnabled: false });
  };

  // ── Table selection ─────────────────────────────────────────────────────────
  const onSelectionChanged = useCallback((params) => {
    if (!params.api) return;
    const ids = params.api.getSelectedNodes().map((node) => node.data.profitCenterDescription);
    setSelectedTableRows(ids);
  }, []);

  const tableRows = useMemo(
    () => assignedPCs.map((item) => ({ ...item, id: item.profitCenterDescription })),
    [assignedPCs],
  );

  const columns = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        field: "supportCheckbox",
        headerName: "",
        sortable: false,
        width: 50,
        suppressSizeToFit: true,
        cellStyle: { textAlign: "center" },
      },
      {
        field: "profitCenterCountry",
        headerName: "Profit Center Country",
        width: 220,
        sortable: true,
        unSortIcon: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        cellRenderer: (params) => (
          <Tooltip title={params.data.profitCenterCountry} arrow>
            <span>{params.data.profitCenterCountry}</span>
          </Tooltip>
        ),
        cellStyle: { textAlign: "left" },
      },
      {
        field: "profitCenterDescription",
        headerName: "Profit Center Description",
        flex: 1,
        minWidth: 280,
        sortable: true,
        unSortIcon: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        cellRenderer: (params) => (
          <Tooltip title={params.data.profitCenterDescription} arrow>
            <span>{params.data.profitCenterDescription}</span>
          </Tooltip>
        ),
        cellStyle: { textAlign: "left" },
      },
      {
        field: "region",
        headerName: "Region",
        flex: 1,
        minWidth: 280,
        sortable: true,
        unSortIcon: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
        },
        cellRenderer: (params) => (
          <Tooltip title={params.data.region || "region"} arrow>
            <span>{params.data.region || "-"}</span>
          </Tooltip>
        ),
        cellStyle: { textAlign: "left" },
      },
    ],
    [],
  );

  if (!user) {
    return null;
  }

  if (loadingMappings) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ mb: 2 }}
      >
        <Link
          underline="always"
          color="inherit"
          href="#"
          onClick={(event) => {
            event.preventDefault();
            onBack();
          }}
          sx={{ cursor: "pointer" }}
        >
          Profit Center Configuration
        </Link>
        <Typography color="text.primary">Edit Profit Centers</Typography>
      </Breadcrumbs>

      {/* User info */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="User Name"
            value={`${user.firstName || ""} ${user.lastName || ""}`}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Email"
            value={user.emailId || ""}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Role"
            value={user.userRole || ""}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      {/* Autocomplete + action buttons on one row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          {selectedRegion && (
            <Chip
              label={`Region: ${selectedRegion}`}
              size="small"
              onDelete={() => {
                setSelectedRegion(null);
                setPendingSelections([]);
              }}
              sx={{ mb: 1 }}
            />
          )}
          <Autocomplete
            multiple
            limitTags={3}
            getLimitTagsText={(more) => `+${more} more`}
            options={selectedRegion ? ["Select All", ...availableOptions] : availableOptions}
            value={selectedRegion ? pendingSelections : []}
            onChange={handleAutocompleteChange}
            disableCloseOnSelect={!!selectedRegion}
            size="small"
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return option.displayLabel || `${option.profitCenterCountry} - ${option.profitCenterDescription}`;
            }}
            isOptionEqualToValue={(option, value) => {
              if (typeof option === "string" || typeof value === "string") return option === value;
              return option.profitCenterCountry === value.profitCenterCountry &&
                option.profitCenterDescription === value.profitCenterDescription;
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                maxHeight: "96px",
                overflowY: "auto",
                alignItems: "flex-start",
                flexWrap: "wrap",
              },
            }}
            renderOption={(props, option, { selected }) => {
              if (!selectedRegion) {
                // Region mode: plain list item, no checkbox
                return (
                  <li {...props}>
                    {option}
                  </li>
                );
              }
              // PC mode: checkbox list item
              const isSelectAll = option === "Select All";
              const allSelected = availableOptions.length > 0 && pendingSelections.length === availableOptions.length;
              return (
                <li {...props}>
                  <Checkbox
                    icon={blankIcon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={isSelectAll ? allSelected : selected}
                    indeterminate={isSelectAll && pendingSelections.length > 0 && !allSelected}
                    size="small"
                  />
                  {isSelectAll ? "Select All" : `${option.profitCenterDescription}`}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={selectedRegion ? "Assign Profit Center Countries" : "Select Region"}
                placeholder={
                  !selectedRegion
                    ? "Select a region first"
                    : pendingSelections.length === 0
                    ? "Select profit center countries to assign"
                    : ""
                }
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Box>

        <ButtonComponent
          bgColor="black"
          color="white"
          label={actionType === "enable" && saving ? "Enabling..." : "Enable"}
          fullWidth={false}
          onClick={handleEnable}
          disabled={pendingSelections.length === 0 || saving}
        />

        <ButtonComponent
          bgColor="#d32f2f"
          color="white"
          label={actionType === "disable" && saving ? "Disabling..." : "Disable"}
          fullWidth={false}
          onClick={handleDisable}
          disabled={selectedTableRows.length === 0 || saving}
        />
      </Box>

      {/* Assigned PCs table */}
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Assigned Profit Centers
      </Typography>
      <DataTable
        ref={gridRef}
        rows={tableRows}
        columns={columns}
        tableHeight={"50vh"}
        suppressMovableColumns={true}
        suppressHorizontalScroll={false}
        suppressPaginationPanel={false}
        pagination={true}
        paginationPageSize={10}
        paginationPageSizeSelector={[5, 10, 20, 50]}
        onSelectionChanged={onSelectionChanged}
        getRowId={(params) => params.data.profitCenterDescription}
      />
    </Box>
  );
};

export default ProfitCenterAssignmentEditor;
